/* Theme-local presentation layer. Product metafields and Boost app settings are unchanged. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.gsrSearchLengthParser = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  const IDS = ['pf_m_::Kettenlaenge::Kettenlaenge', 'pf_m_::Armbandlaenge::Armbandlaenge'];
  const target = id => IDS.includes(id);
  const num = s => Number(s.replace(',', '.'));
  const fmt = n => String(Math.round(n * 100) / 100).replace('.', ',');
  const n = '(\\d+(?:[.,]\\d+)?)';
  const plus = new RegExp('^' + n + '\\s*cm(?:\\s+(?:Basiskette|Basislänge|Kettenlänge))?\\s*\\+\\s*' + n + '\\s*cm(?:\\s+Verl\\S*)?$', 'i');
  const range = new RegExp('^' + n + '\\s*[-–—]\\s*' + n + '\\s*cm(?:,?\\s*(?:flexibel|stufenlos)\\s+einstellbar)?$', 'i');
  const fixed = new RegExp('^' + n + '\\s*cm$', 'i');
  const flex = new RegExp('^' + n + '\\s*cm,?\\s*flexibel\\s+einstellbar$', 'i');
  const multiple = new RegExp('^' + n + '\\s*/\\s*' + n + '\\s*cm(?:\\s+Basiskette)?\\s*\\+\\s*' + n + '\\s*cm(?:\\s+Verl\\S*)?$', 'i');
  function normalize(value) {
    const raw = String(value ?? '').replace(/\u00a0/g, ' ').trim().replace(/\s+/g, ' ');
    let m;
    if ((m = raw.match(plus))) {
      const lo = num(m[1]), hi = Math.round((lo + num(m[2])) * 100) / 100;
      return { key: `range:${lo}:${hi}`, label: `${fmt(lo)}–${fmt(hi)} cm`, lo, hi, rank: 1 };
    }
    if ((m = raw.match(range)) && num(m[1]) <= num(m[2])) {
      const lo = num(m[1]), hi = num(m[2]);
      return { key: `range:${lo}:${hi}`, label: `${fmt(lo)}–${fmt(hi)} cm`, lo, hi, rank: 1 };
    }
    if ((m = raw.match(fixed))) {
      const lo = num(m[1]);
      return { key: `fixed:${lo}`, label: `${fmt(lo)} cm`, lo, hi: lo, rank: 0 };
    }
    if ((m = raw.match(flex))) {
      const lo = num(m[1]);
      return { key: `flex:${lo}`, label: `${fmt(lo)} cm · verstellbar`, lo, hi: lo, rank: 2 };
    }
    // Keep two separate chain lengths separate: do not invent one continuous range.
    if ((m = raw.match(multiple))) {
      return { key: 'raw:' + raw, label: `${fmt(num(m[1]))} / ${fmt(num(m[2]))} cm + ${fmt(num(m[3]))} cm`, lo: Infinity, hi: Infinity, rank: 3 };
    }
    if (/^flexibel einstellbar$/i.test(raw)) {
      return { key: 'flex:unknown', label: 'Flexibel einstellbar', lo: Infinity, hi: Infinity, rank: 4 };
    }
    return { key: 'raw:' + raw, label: raw, lo: Infinity, hi: Infinity, rank: 5 };
  }
  const compare = (a, b) => {
    const x = normalize(a.key), y = normalize(b.key);
    return (x.lo - y.lo) || (x.hi - y.hi) || (x.rank - y.rank) || x.label.localeCompare(y.label, 'de', { numeric: true });
  };
  function createRegistry() {
    const facets = new Map();
    const get = id => { if (!facets.has(id)) facets.set(id, new Map()); return facets.get(id); };
    function group(id, values) {
      const registry = get(id), visible = new Map();
      for (const row of values) {
        const info = normalize(row.key);
        if (!registry.has(info.key)) registry.set(info.key, { ...info, representative: row.key, members: new Set() });
        const known = registry.get(info.key);
        known.members.add(row.key);
        if (!visible.has(info.key)) visible.set(info.key, { ...row, key: known.representative, doc_count: 0 });
        visible.get(info.key).doc_count += Number(row.doc_count) || 0;
      }
      return [...visible.values()].sort(compare);
    }
    function expand(id, values) {
      const registry = get(id), out = new Set();
      for (const value of Array.isArray(values) ? values : values == null ? [] : [values]) {
        const known = registry.get(normalize(value).key);
        for (const raw of known ? known.members : [value]) out.add(raw);
      }
      return [...out];
    }
    return { facets, group, expand };
  }
  function install(w) {
    if (w.gsrLengthFilters || !w.Filter || !w.FilterOptionItem) return;
    const registry = createRegistry();
    const api = w.FilterApi, globals = w.Globals;
    const baseSetParam = api.setParam;
    const baseModify = w.Filter.prototype.modifyData;
    const baseSort = w.FilterOption.prototype.sortValues;
    const baseLabel = w.FilterOptionItem.prototype.buildLabel;
    const baseApplied = w.FilterOptionItem.prototype.isAppliedFilter;
    const baseApply = w.FilterOptionItem.prototype.onApplyFilter;
    const baseRefine = w.FilterRefineBy.prototype.setData;
    const baseClear = w.FilterClearButton.prototype.onClearSingleValue;
    const baseRender = w.FilterOption.prototype.render;
    const same = (a, b) => a.length === b.length && a.every(v => b.includes(v));
    let canonicalizationQueued = false;
    api.setParam = function (id, values) {
      return baseSetParam.call(this, id, target(id) && Array.isArray(values) ? registry.expand(id, values) : values);
    };
    w.Filter.prototype.modifyData = function (input) {
      const data = baseModify.call(this, input) || input;
      for (const option of data?.filter?.options || []) {
        if (!target(option.filterOptionId) || !Array.isArray(option.values)) continue;
        option.values = registry.group(option.filterOptionId, option.values);
        option.useAndCondition = false;
      }
      // Old links may select only one spelling. Canonicalize them to the complete group.
      if (!canonicalizationQueued) {
        const pending = IDS.map(id => {
          const current = globals.queryParams[id];
          if (!Array.isArray(current)) return null;
          const expanded = registry.expand(id, current);
          return same(current, expanded) ? null : { id, current: [...current], expanded };
        }).filter(Boolean);
        if (pending.length) {
          canonicalizationQueued = true;
          queueMicrotask(() => {
            canonicalizationQueued = false;
            let changed = false;
            for (const p of pending) {
              if (!same(globals.queryParams[p.id] || [], p.current)) continue;
              baseSetParam.call(api, p.id, p.expanded); changed = true;
            }
            if (changed) { baseSetParam.call(api, 'page', 1); api.applyFilter('filter'); }
          });
        }
      }
      return data;
    };
    w.FilterOption.prototype.sortValues = function (values) {
      if (target(this.filterOptionId)) return values.sort(compare);
      return baseSort.apply(this, arguments);
    };
    w.FilterOptionItem.prototype.buildLabel = function () {
      const option = this.filterOption || this.parent;
      if (target(option?.filterOptionId)) return w.Utils.stripHtml(normalize(this.value ?? this.label).label);
      return baseLabel.apply(this, arguments);
    };
    w.FilterOptionItem.prototype.isAppliedFilter = function () {
      if (!target(this.parent?.filterOptionId)) return baseApplied.apply(this, arguments);
      const selected = globals.queryParams[this.parent.filterOptionId];
      return Array.isArray(selected) && selected.some(value => normalize(value).key === normalize(this.value).key);
    };
    function setSelection(id, values, eventType) {
      globals.internalClick = true;
      api.setParam(id, values);
      api.setParam(id + '_and_condition', null);
      api.setParam('page', 1);
      api.applyFilter(eventType, { filterOptionId: id, filterOptionValue: values });
    }
    w.FilterOptionItem.prototype.onApplyFilter = function () {
      const id = this.parent?.filterOptionId;
      if (!target(id)) return baseApply.apply(this, arguments);
      const old = Array.isArray(globals.queryParams[id]) ? globals.queryParams[id] : [];
      const key = normalize(this.value).key;
      const next = this.isSelected ? old.filter(raw => normalize(raw).key !== key) : [...old, this.value];
      setSelection(id, next, 'filter');
    };
    w.FilterRefineBy.prototype.setData = function () {
      baseRefine.apply(this, arguments);
      const seen = new Set(), discarded = new Set();
      this.refineByItems = (this.refineByItems || []).filter(item => {
        if (!target(item.filterOptionId)) return true;
        const key = item.filterOptionId + '|' + normalize(item.filterItemId).key;
        if (seen.has(key)) { discarded.add(item); return false; }
        seen.add(key); return true;
      });
      if (Array.isArray(this.children)) this.children = this.children.filter(item => !discarded.has(item));
    };
    w.FilterClearButton.prototype.onClearSingleValue = function () {
      const id = this.parent?.filterOptionId;
      if (!target(id)) return baseClear.apply(this, arguments);
      const key = normalize(this.parent.filterItemId).key;
      const selected = Array.isArray(globals.queryParams[id]) ? globals.queryParams[id] : [];
      setSelection(id, selected.filter(raw => normalize(raw).key !== key), 'clear');
    };
    w.FilterOption.prototype.render = function () {
      baseRender.apply(this, arguments);
      if (!target(this.filterOptionId)) return;
      const el = this.$element?.[0], content = this.$filterOptionContentElement?.[0];
      if (!el || !content) return;
      el.classList.add('gsr-length-filter');
      if (!content.querySelector('.gsr-length-help')) {
        const note = w.document.createElement('p');
        note.className = 'gsr-length-help';
        note.textContent = 'Längenbereiche inklusive Verlängerung.';
        content.insertBefore(note, content.firstChild);
      }
    };
    w.gsrLengthFilters = { normalize, registry, ids: IDS };
  }
  return { normalize, compare, createRegistry, install, ids: IDS };
});
